import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEnergyAssets } from "../../hooks";
import { UploadReadingModal } from "./modals/UploadMeterReadingModal";

export const ElectricUtilityTable = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const { data: assetList, isPending: isLoading } = useEnergyAssets(
    "Electric Utility",
    currentPage
  );

  // Define columns based on the Electric Utility image
  const columns: TableColumn<any>[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.asset?.assetNo ?? "NA"}</span>
        </div>
      ),
    },
    {
      key: "assetName",
      header: "Asset Name",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.asset?.assetName ?? "NA"}</span>
        </div>
      ),
    },
    {
      key: "deviceNumber",
      header: "Device Number",
      type: "text",
    },
    {
      key: "systemNetwork",
      header: "System/Network",
      type: "text",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {row.asset?.systemName ?? row.asset?.networkName ?? "NA"}
          </span>
        </div>
      ),
      headerTooltip:
        "The parent system or network this asset is a component of.",
    },
    {
      key: "units",
      header: "Units",
      type: "text",
    },
    {
      key: "previousReading",
      header: "Previous Reading",
      type: "text",
    },
    {
      key: "previousReadingDate",
      header: "Previous Date",
      type: "text",
    },
    {
      key: "currentReading",
      header: "Current Reading",
      type: "text",
    },
    {
      key: "currentReadingDate",
      header: "Current Date",
      type: "text",
    },
    {
      key: "consumption",
      header: "Consumption",
      type: "text",
    },
    // {
    //   key: "totalCharges",
    //   header: "Total Charges",
    //   type: "text",
    //   sortable: false,
    // },
    {
      key: "lastUpdate",
      header: "Last Update",
      type: "text",
    },
  ];

  const handleView = (row: any) => {
    navigate(`/ax/asset-registry/assets/view/${row.asset?.id}/${row.id}`);
  };

  const handleUploadReading = (row: any) => {
    setSelectedAsset(row);
    setIsModalOpen(true);
  };

  const handleSubmitReading = async (data: any) => {
    setSelectedAsset(null);
  };

  // Define actions for the table
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: handleView,
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Upload Reading",
      onClick: handleUploadReading,
      icon: "Upload",
      variant: "outline",
    },
  ];

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Calculate pagination info
  const totalItems = assetList?.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      <GenericTable
        data={assetList?.result ?? []}
        columns={columns}
        rowKey="assetId"
        actions={actions}
        actionsType="icons"
        emptyMessage="No electric utility assets found"
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem,
          endItem,
        }}
        onPageChange={handlePageChange}
      />

      {/* Upload Reading Modal */}
      {selectedAsset && (
        <UploadReadingModal
          open={isModalOpen}
          onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) setSelectedAsset(null);
          }}
          assetInfo={selectedAsset}
          assetType="electric-utility"
          onSubmit={handleSubmitReading}
        />
      )}
    </div>
  );
};
